/**
 * NavBadge - Notification badge for navigation items
 *
 * Matches the NotificationBell badge style for consistency.
 * Hidden when count is 0.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface NavBadgeProps {
  count: number;
  className?: string;
  tooltip?: string;
  holidayMode?: boolean;
}

export const NavBadge = ({
  count,
  className,
  tooltip,
  holidayMode = false,
}: NavBadgeProps) => {
  if (count <= 0) return null;

  const displayCount = count > 99 ? '99+' : count.toString();

  const badge = (
    <span
      className={cn(
        // Position - matching NotificationBell placement
        'absolute -top-0.5 -right-2',
        // Centering
        'flex items-center justify-center',
        // Size - matches NotificationBell exactly
        'min-w-[16px] h-[16px] px-1',
        'rounded-full',
        // Typography - matches NotificationBell
        'text-[10px] font-bold leading-none',
        'ring-2',
        'shadow-sm',
        // Color and ring - changes based on holiday mode
        holidayMode
          ? 'bg-white text-red-700 ring-red-700'
          : 'bg-red-500 text-white ring-[#1e1b4b]',
        className,
      )}
    >
      {displayCount}
    </span>
  );

  if (!tooltip) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-sm">
        <p className="text-xs">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};
